import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../api/client";

function createSessionToken() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `mg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function GoogleAddressAutocomplete({
  value,
  onChange,
  onSelect,
  onReadyChange,
  placeholder,
  statusTexts,
  disabled = false,
  className = "",
  required = false,
  languageCode = "en",
  regionCode = "",
}) {
  const blurTimerRef = useRef(null);
  const requestIdRef = useRef(0);
  const sessionTokenRef = useRef(createSessionToken());
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const readyHint = useMemo(
    () =>
      statusTexts?.readyHint ||
      "Start typing and choose a real address from Google suggestions.",
    [statusTexts]
  );

  useEffect(() => {
    onReadyChange?.(true);
    setStatusMessage("");

    return () => {
      if (blurTimerRef.current) {
        window.clearTimeout(blurTimerRef.current);
      }
    };
  }, [onReadyChange]);

  useEffect(() => {
    if (disabled) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    const query = String(value || "").trim();

    if (query.length < 3) {
      setSuggestions([]);
      setHighlightedIndex(-1);
      setLoading(false);
      setStatusMessage(query.length > 0 ? readyHint : "");
      return;
    }

    const currentRequestId = ++requestIdRef.current;

    const timer = window.setTimeout(async () => {
      setLoading(true);
      setStatusMessage("");

      try {
        const params = new URLSearchParams({
          input: query,
          language: languageCode || "en",
        });

        if (regionCode) {
          params.set("region", regionCode);
        }

        if (sessionTokenRef.current) {
          params.set("session_token", sessionTokenRef.current);
        }

        const response = await api.apiGet(`address-autocomplete?${params.toString()}`);

        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        const items = Array.isArray(response?.data) ? response.data : [];
        setSuggestions(items);
        setHighlightedIndex(items.length > 0 ? 0 : -1);
        setStatusMessage(items.length === 0 ? statusTexts?.noResults || "" : "");
        onReadyChange?.(true);
      } catch (error) {
        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        setSuggestions([]);
        setHighlightedIndex(-1);
        setStatusMessage(
          error?.message ||
            statusTexts?.loadFailed ||
            "Address search is temporarily unavailable."
        );
        onReadyChange?.(false);
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
    };
  }, [disabled, languageCode, onReadyChange, readyHint, regionCode, statusTexts, value]);

  async function selectSuggestion(suggestion) {
    if (!suggestion?.place_id) {
      return;
    }

    setLoading(true);
    setStatusMessage("");

    try {
      const params = new URLSearchParams({
        language: languageCode || "en",
      });

      if (sessionTokenRef.current) {
        params.set("session_token", sessionTokenRef.current);
      }

      const response = await api.apiGet(
        `address-details/${encodeURIComponent(suggestion.place_id)}?${params.toString()}`
      );

      const selection = response?.data;

      if (selection) {
        onSelect?.(selection);
      }

      setSuggestions([]);
      setHighlightedIndex(-1);
      setIsFocused(false);
      sessionTokenRef.current = createSessionToken();
      onReadyChange?.(true);
    } catch (error) {
      setStatusMessage(
        error?.message ||
          statusTexts?.loadFailed ||
          "Address search is temporarily unavailable."
      );
      onReadyChange?.(false);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(event) {
    if (suggestions.length === 0) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
      return;
    }

    if (event.key === "Enter" && highlightedIndex >= 0) {
      event.preventDefault();
      selectSuggestion(suggestions[highlightedIndex]);
      return;
    }

    if (event.key === "Escape") {
      setSuggestions([]);
      setHighlightedIndex(-1);
    }
  }

  return (
    <div className="position-relative">
      <input
        type="text"
        className={className}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (blurTimerRef.current) {
            window.clearTimeout(blurTimerRef.current);
          }
          setIsFocused(true);
        }}
        onBlur={() => {
          blurTimerRef.current = window.setTimeout(() => {
            setIsFocused(false);
            setSuggestions([]);
            setHighlightedIndex(-1);
          }, 120);
        }}
        autoComplete="street-address"
        disabled={disabled}
        required={required}
      />

      {isFocused && suggestions.length > 0 ? (
        <div
          className="list-group shadow-sm position-absolute w-100"
          style={{ zIndex: 30, top: "calc(100% + 6px)", maxHeight: "260px", overflowY: "auto" }}
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.place_id}
              type="button"
              className={`list-group-item list-group-item-action ${
                index === highlightedIndex ? "active" : ""
              }`}
              onMouseDown={(event) => {
                event.preventDefault();
                selectSuggestion(suggestion);
              }}
            >
              <div className="fw-semibold">
                {suggestion.main_text || suggestion.text}
              </div>
              {suggestion.secondary_text ? (
                <div className={`small ${index === highlightedIndex ? "text-white-50" : "text-muted"}`}>
                  {suggestion.secondary_text}
                </div>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}

      {statusMessage ? (
        <div className="small text-muted mt-2">{statusMessage}</div>
      ) : (
        <div className="small text-muted mt-2">
          {loading ? statusTexts?.loading || "Loading address suggestions..." : readyHint}
        </div>
      )}
    </div>
  );
}
